import { VehicleAttributeCategory, VehicleAttributeValueEntity } from 'src/entities/VehicleAttributeValue.entity';

/**
 * 車両条件の選択肢を引く関数。`useVehicleAttributeOptions` の `optionsOf` の型。
 *
 * Why not 純粋な util が hook から型を import する: 依存の向きが逆になるので、
 * 選択肢の組み立てを持つこちら側に型を置き、hook がそれを満たす形にしてある。
 */
export type VehicleAttributeOptionsOf = (
  category: VehicleAttributeCategory,
  currentValues: readonly (string | null | undefined)[],
) => string[];

/**
 * 選択肢に「編集中のレコードが今持っている値」を足す。#329 P8・設計書 §5-4
 *
 * 無効にした値・改名した値を持つ既存データを編集できなくしないため。
 * とくに MultiCheckbox は options に無い値を setValue で落とすので、ここを通さないと
 * **開いて保存しただけで既存データから値が消える**。
 *
 * Why not 選択肢に無い現在値を捨てる: 絞り込み（`useOrderAttrFilter.ts`）は捨てるのが正しい。
 * 読めない値を条件に残すと「外した覚えのない案件が消える」＝**利用者が気づけない**からである。
 * 一方この関数は編集画面用で、値を落とすと**保存しただけで既存データが変わる**（決定 5-4 違反）。
 * 理由が逆なので、どちらかに寄せないこと。
 *
 * Why not MultiCheckBox 側で落とさないようにする: あの effect は
 * 「提出する値は画面に出ている値の部分集合」という不変条件を保っていて、
 * CSV 取込の不正値を落とす既存の意図もそこに乗っている。壊さずに満たせるので
 * 呼び出し側で選択肢を組み立てる。
 */
export const withCurrentValues = (
  options: readonly string[] | undefined,
  currentValues: readonly (string | null | undefined)[],
): string[] => {
  const known = new Set(options ?? []);
  const missing: string[] = [];

  currentValues.forEach((current) => {
    if (!current) return;
    if (known.has(current)) return;

    known.add(current);
    missing.push(current);
  });

  return [...(options ?? []), ...missing];
};

/**
 * 画面に出す車両条件の選択肢を組み立てる。#329 P8
 *
 * 有効な値を position 順に並べ、選択肢に無い現在値を末尾に足す（§5-4）。
 *
 * @param values そのカテゴリのマスタ（無効な値も含む）。未取得なら undefined
 * @param currentValues 編集中のレコードが今持っている値。
 *   省略可にしない（渡し忘れが型で出ないと §5-4 の穴になる。`optionsOf` と同じ方針）。
 *   本当に不要な呼び出し側だけが明示的に [] を渡す。
 */
export const buildVehicleAttributeOptions = (
  values: VehicleAttributeValueEntity[] | undefined,
  currentValues: readonly (string | null | undefined)[],
): string[] => {
  const options = [...(values ?? [])]
    .filter((it) => it.active)
    .sort((a, b) => a.position - b.position)
    .map((it) => it.name);

  return withCurrentValues(options, currentValues);
};
